/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  makeStyles,
} from '@material-ui/core';
import { Redirect } from 'react-router';
import { useSelector } from 'react-redux';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import esLocale from 'date-fns/locale/es';

import Request from '../../../requests/api/blog';
import slugify from '../../../utils/slug';
import ClusterRequest from '../../../requests/api/cluster';
import { BLOG as CLUSTER_BLOG } from '../../../utils/const/clusterTypes';
import ImageUploadField from '../../../components/ImageUploadField';
import RichTextEditor from '../../../components/RichTextEditor';
import SeoFields from '../../../components/SeoFields';
import { BLOG_COVERS, BLOG_OG, BLOG_CONTENT } from '../../../utils/const/uploadFolders';
import {
  SEO_TITLE_MAX_LENGTH,
  OG_TITLE_MAX_LENGTH,
} from '../../../utils/const/seoLimits';

const useStyles = makeStyles(() => ({
  root: {},
}));

function CreateForm({ className, ...rest }) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [published, setPublished] = useState(false);
  const [featured, setFeatured] = useState(false);
  const [isMarkdown, setIsMarkdown] = useState(false);
  const [blogContent, setBlogContent] = useState('');
  const [clusters, setClusters] = useState([]);
  const [createdAt] = useState(new Date());

  const account = useSelector((state) => state.account);

  useEffect(() => {
    const fetchClusters = async () => {
      const response = await ClusterRequest.getClusters(CLUSTER_BLOG);
      if (response.data?.data) {
        setClusters(response.data.data);
      }
    };
    fetchClusters();
  }, []);

  const handleRadioChange = (event) => {
    setIsMarkdown(event.target.value === 'markdown');
  };

  if (submitSuccess) {
    return <Redirect to="/administracion/blog" />;
  }

  return (
    <Formik
      initialValues={{
        userId: account.user.id,
        clusterId: '',
        title: '',
        excerpt: '',
        slug: '',
        content: '',
        meta: '',
        tags: '',
        thumbnail: '',
        seoTitle: '',
        metaDescription: '',
        primaryKeyword: '',
        secondaryKeywords: '',
        ogTitle: '',
        ogImage: '',
        publishedDate: createdAt,
        published,
        isMarkdown,
      }}
      validationSchema={Yup.object().shape({
        clusterId: Yup.number().required('Selecciona un cluster'),
        title: Yup.string().max(255).required('Debe ingresar un título'),
        excerpt: Yup.string().required('Debe ingresar un extracto'),
        slug: Yup.string().max(255).required('Debe ingresar un slug'),
        meta: Yup.string().required('Debe ingresar meta keywords'),
        tags: Yup.string().required('Debe ingresar al menos un tag'),
        metaDescription: Yup.string().max(160).required('Debe ingresar una meta descripción'),
        primaryKeyword: Yup.string().max(100).required('Debe ingresar la keyword principal'),
        seoTitle: Yup.string().max(SEO_TITLE_MAX_LENGTH),
        ogTitle: Yup.string().max(OG_TITLE_MAX_LENGTH),
        publishedDate: Yup.date().typeError('Fecha inválida').required('Debe ingresar una fecha de publicación'),
      })}
      onSubmit={async (values, {
        setErrors,
        setStatus,
        setSubmitting,
      }) => {
        try {
          // Make API request
          const data = {
            userId: account.user.id,
            clusterId: values.clusterId,
            title: values.title,
            excerpt: values.excerpt,
            slug: values.slug,
            content: isMarkdown ? values.content : blogContent,
            meta: values.meta,
            tags: values.tags,
            thumbnail: values.thumbnail,
            seoTitle: values.seoTitle,
            metaDescription: values.metaDescription,
            primaryKeyword: values.primaryKeyword,
            secondaryKeywords: values.secondaryKeywords
              ? values.secondaryKeywords.split(',').map((k) => k.trim()).filter(Boolean)
              : [],
            ogTitle: values.ogTitle,
            ogImage: values.ogImage || values.thumbnail,
            publishedDate: values.publishedDate,
            published,
            featured,
            isMarkdown,
          };
          const response = await Request.postBlogCreate({ blog: data });

          const res = response.data;
          if (res.success) {
            setStatus({ success: true });
            setSubmitting(false);
            setSubmitSuccess(true);
            enqueueSnackbar('Cambios guardados', {
              variant: 'success',
            });
          } else {
            setStatus({ success: false });
            setErrors({ submit: res.data.errorMessage });
            enqueueSnackbar(res.data.errorMessage, {
              variant: 'warning',
              action: <Button href="/administracion/blog">Volver a blogs</Button>,
            });
          }
        } catch (error) {
          setStatus({ success: false });
          setErrors({ submit: error.message });
          setSubmitting(false);
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        touched,
        values,
      }) => (
        <form
          className={clsx(classes.root, className)}
          onSubmit={handleSubmit}
          {...rest}
        >
          <Card>
            <CardContent>
              <Grid
                container
                spacing={3}
              >
                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <TextField
                    error={Boolean(touched.title && errors.title)}
                    fullWidth
                    helperText={touched.title && errors.title}
                    label="Título"
                    name="title"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    value={values.title}
                    variant="outlined"
                  />
                </Grid>
                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <TextField
                    error={Boolean(touched.excerpt && errors.excerpt)}
                    fullWidth
                    helperText={touched.excerpt && errors.excerpt}
                    label="Extracto"
                    name="excerpt"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    value={values.excerpt}
                    variant="outlined"
                  />
                </Grid>
                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <TextField
                    error={Boolean(touched.slug && errors.slug)}
                    fullWidth
                    helperText={touched.slug && errors.slug}
                    label="Slug"
                    name="slug"
                    onBlur={handleBlur}
                    onChange={(event) => setFieldValue('slug', slugify(event.target.value))}
                    required
                    value={values.slug}
                    variant="outlined"
                  />
                </Grid>
                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <TextField
                    error={Boolean(touched.clusterId && errors.clusterId)}
                    fullWidth
                    helperText={touched.clusterId && errors.clusterId}
                    label="Cluster"
                    name="clusterId"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    select
                    value={values.clusterId}
                    variant="outlined"
                  >
                    {clusters.map((cluster) => (
                      <MenuItem key={cluster.id} value={cluster.id}>
                        {cluster.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid
                  item
                  md={12}
                  xs={12}
                >
                  <FormControl>
                    <FormLabel id="radio-blog-format">Formato</FormLabel>
                    <RadioGroup
                      aria-labelledby="radio-blog-format"
                      defaultValue="editor"
                      name="radio-blog-group"
                      onChange={handleRadioChange}
                    >
                      <FormControlLabel value="editor" control={<Radio />} label="Editor" />
                      <FormControlLabel value="markdown" control={<Radio />} label="Markdown" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid
                  item
                  md={12}
                  xs={12}
                >
                  {isMarkdown
                    ? (
                      <TextField
                        error={Boolean(touched.content && errors.content)}
                        fullWidth
                        helperText={touched.content && errors.content}
                        label="Contenido"
                        name="content"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        required
                        multiline
                        value={values.content}
                        variant="outlined"
                      />
                    ) : (
                      <RichTextEditor value={blogContent} onChange={setBlogContent} folder={BLOG_CONTENT} />
                    )}
                </Grid>
                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <TextField
                    error={Boolean(touched.meta && errors.meta)}
                    fullWidth
                    helperText={touched.meta && errors.meta}
                    label="Meta keywords"
                    name="meta"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    value={values.meta}
                    variant="outlined"
                  />
                </Grid>
                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <TextField
                    error={Boolean(touched.tags && errors.tags)}
                    fullWidth
                    helperText={touched.tags && errors.tags}
                    label="Tags"
                    name="tags"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    value={values.tags}
                    variant="outlined"
                  />
                </Grid>
                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <ImageUploadField
                    error={Boolean(touched.thumbnail && errors.thumbnail)}
                    folder={BLOG_COVERS}
                    helperText={touched.thumbnail && errors.thumbnail}
                    label="Imagen de portada"
                    name="thumbnail"
                    onBlur={handleBlur}
                    onChange={(url) => setFieldValue('thumbnail', url)}
                    required
                    value={values.thumbnail}
                  />
                </Grid>
                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <TextField
                    disabled
                    fullWidth
                    helperText="Se registra al guardar el blog"
                    label="Fecha de creación"
                    value={format(createdAt, 'dd-MM-yyyy')}
                    variant="outlined"
                  />
                </Grid>
                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={esLocale}
                  >
                    <DatePicker
                      inputFormat="dd-MM-yyyy"
                      views={['year', 'month', 'day']}
                      label="Fecha de publicación"
                      value={values.publishedDate}
                      onChange={(value) => setFieldValue('publishedDate', value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          variant="outlined"
                          error={Boolean(touched.publishedDate && errors.publishedDate)}
                          helperText={(touched.publishedDate && errors.publishedDate)
                            || 'Por defecto la fecha de creación. Se puede editar manualmente.'}
                          onBlur={handleBlur}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <FormControl component="fieldset" variant="standard">
                    <FormLabel component="legend">Destacado</FormLabel>
                    <Switch
                      checked={featured}
                      onChange={() => setFeatured(!featured)}
                      inputProps={{ 'aria-label': 'controlled' }}
                    />
                  </FormControl>
                </Grid>
                <SeoFields
                  errors={errors}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  ogImageFolder={BLOG_OG}
                  setFieldValue={setFieldValue}
                  touched={touched}
                  values={values}
                />
                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <FormControl component="fieldset" variant="standard">
                    <FormLabel component="legend">Publicar</FormLabel>
                    <Switch
                      checked={published}
                      onChange={() => setPublished(!published)}
                      inputProps={{ 'aria-label': 'controlled' }}
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Box mt={2}>
                <Button
                  variant="contained"
                  color="secondary"
                  type="submit"
                  disabled={isSubmitting}
                >
                  Guardar
                </Button>
              </Box>
            </CardContent>
          </Card>
        </form>
      )}
    </Formik>
  );
}

CreateForm.propTypes = {
  className: PropTypes.string,
};

export default CreateForm;
